import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound } from "lucide-react";

import { BrandMark } from "@/components/BrandMark";
import { api, ApiError } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import { useLocale } from "@/lib/locale";

const TEXT = {
  it: {
    kicker: "Sicurezza",
    title: "Scegli una nuova password",
    intro:
      "Stai usando una password temporanea. Scegline una tua: serve a proteggere i certificati e i dati dei cantieri.",
    current: "Password attuale",
    next: "Nuova password",
    confirm: "Ripeti la nuova password",
    submit: "Salva e continua",
    busy: "Salvataggio…",
    mismatch: "Le due password non coincidono.",
    short: "Servono almeno 8 caratteri.",
    wrong: "La password attuale non è corretta.",
    generic: "Non è stato possibile salvare. Riprova.",
  },
  en: {
    kicker: "Security",
    title: "Choose a new password",
    intro:
      "You're signed in with a temporary password. Set your own — it protects the certificates and the project records.",
    current: "Current password",
    next: "New password",
    confirm: "Repeat the new password",
    submit: "Save and continue",
    busy: "Saving…",
    mismatch: "The two passwords don't match.",
    short: "At least 8 characters, please.",
    wrong: "The current password isn't correct.",
    generic: "Couldn't save. Please try again.",
  },
  es: {
    kicker: "Seguridad",
    title: "Elige una nueva contraseña",
    intro:
      "Estás usando una contraseña temporal. Crea la tuya: protege los certificados y los datos de obra.",
    current: "Contraseña actual",
    next: "Nueva contraseña",
    confirm: "Repite la nueva contraseña",
    submit: "Guardar y continuar",
    busy: "Guardando…",
    mismatch: "Las dos contraseñas no coinciden.",
    short: "Mínimo 8 caracteres.",
    wrong: "La contraseña actual no es correcta.",
    generic: "No se pudo guardar. Inténtalo de nuevo.",
  },
} as const;

export default function ChangePasswordPage() {
  const { locale } = useLocale();
  const tx = TEXT[locale];
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    if (form.next.length < 8) return setError(tx.short);
    if (form.next !== form.confirm) return setError(tx.mismatch);

    setBusy(true);
    try {
      await api.changePassword(form.current, form.next);
      const next = await refresh();
      if (!next?.mustChangePassword) navigate("/projects");
      else setError(tx.generic);
    } catch (err) {
      const status = err instanceof ApiError ? err.status : 0;
      const body = err instanceof ApiError ? err.message.toLowerCase() : "";
      setError(
        status === 401 && body.includes("current") ? tx.wrong : tx.generic,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app-surface grid min-h-screen place-items-center px-5 py-10">
      <form onSubmit={onSubmit} className="glass-panel-strong w-full max-w-sm p-7">
        <div className="flex flex-col items-center text-center">
          <BrandMark className="h-14 w-14" />
          <div className="brand-kicker mt-3">{tx.kicker}</div>
          <h1 className="mt-1 font-serif text-lg text-paper">{tx.title}</h1>
          <p className="mt-2 text-[0.74rem] leading-relaxed text-ice/70">
            {tx.intro}
          </p>
        </div>

        <hr className="hairline my-6" />

        <label className="block">
          <span className="field-label">{tx.current}</span>
          <input
            className="field"
            type="password"
            autoComplete="current-password"
            value={form.current}
            onChange={(e) => setForm({ ...form, current: e.target.value })}
            required
          />
        </label>

        <label className="mt-3 block">
          <span className="field-label">{tx.next}</span>
          <input
            className="field"
            type="password"
            autoComplete="new-password"
            minLength={8}
            value={form.next}
            onChange={(e) => setForm({ ...form, next: e.target.value })}
            required
          />
        </label>

        <label className="mt-3 block">
          <span className="field-label">{tx.confirm}</span>
          <input
            className="field"
            type="password"
            autoComplete="new-password"
            minLength={8}
            value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })}
            required
          />
        </label>

        {error && <p className="mt-3 text-xs text-pink">{error}</p>}

        <button
          type="submit"
          disabled={busy}
          className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-magenta px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-paper transition hover:brightness-110 disabled:opacity-60"
        >
          <KeyRound className="h-3.5 w-3.5" />
          {busy ? tx.busy : tx.submit}
        </button>
      </form>
    </div>
  );
}
